import React from 'react';
import { connect } from 'react-redux';
import { BetaLabel } from 'components/BetaProgram';
import Item from 'components/_shared/dropdown_menu/dropdown_menu_item';
import List from 'components/_shared/dropdown_menu/dropdown_menu_list';
import questionTypes from 'constants/questionTypes';
import { hasGlobalFeature } from 'selectors';
import { useFeatureFlags } from 'components/shared/hooks';
import {
  AlignLeft,
  Calendar,
  Check,
  Copy,
  FileText,
  Film,
  Heart,
  HelpCircle,
  Image,
  Mic,
  Star,
  Lock,
} from 'react-feather';
import AISvg from 'components/shared/svg/AISvg';
import SeismicSvg from './icons/SeismicSvg';
import FlipCardSvg from './icons/FlipcardSvg';
import RevealSvg from './icons/RevealSvg';
import GongSvg from './icons/GongSvg';

const Option = ({
  onClick,
  nestedOptions,
  icon,
  label,
  isLocked,
  isNew,
  isBeta,
  pendoClassName,
}) => (
  <Item
    onClick={!isLocked && onClick}
    parent={!!nestedOptions}
    isLocked={isLocked}
    pendoClassName={pendoClassName}
  >
    {isNew && <b className="mr-4 ">{t('lessons.editor.new')}</b>}
    {icon}
    {label}
    {isLocked && <Lock className="mr-0" />}
    {nestedOptions}
    {isBeta && <BetaLabel className="ml-8" />}
  </Item>
);

const menuSubcategoryLabel = 'pl-10 pt-8 pb-6 c-d fz-12 fw-b';

const Divider = () => (
  <div className="NewDropDown-divider">
    <div className="NewDropDown-divider-inner" />
  </div>
);

const Menu = props => {
  const handleClick = e => {
    e.stopPropagation(); // stop propagating the event to prevent any side effects
    return props.optionClickCallback;
  };

  const questionTypesList = (
    <List alignment="bottom" dark animation="slideUp">
      {questionTypes.map(({ type, label, model, pendoClassName }) => (
        <Option
          key={type}
          label={label}
          onClick={e => handleClick(e)(model)}
          pendoClassName={pendoClassName}
        />
      ))}
      <hr />
      <Option
        label={t('lessons.editor.elements.randomized_questions')}
        onClick={e => handleClick(e)('QuestionBank')}
        pendoClassName="trk_select_learning_add-question-bank-element"
      />
      <Option
        key="upload_response"
        label={t('lessons.editor.elements.upload_response')}
        onClick={e => handleClick(e)('Questions::UploadResponse')}
        pendoClassName="trk_select_learning_add-upload-response-element"
      />
    </List>
  );

  const knowledgeCheckTypesList = (
    <List alignment="bottom" dark animation="slideUp">
      <Option
        label={t('lessons.editor.elements.multiple_choice')}
        onClick={e => handleClick(e)('Questions::KnowledgeCheck')}
        pendoClassName="trk_select_learning_add-knowledge-check-multiple-choice-element"
      />
      <Option
        label={t('lessons.editor.elements.matching')}
        onClick={e => handleClick(e)('Questions::Matching')}
        pendoClassName="trk_select_learning_add-knowledge-check-matching-element"
      />
      <Option
        label={t('lessons.editor.elements.sorting')}
        onClick={e => handleClick(e)('Questions::Sorting')}
        pendoClassName="trk_select_learning_add-knowledge-check-sorting-element"
      />
    </List>
  );

  const practiceTypesList = (
    <List alignment="bottom" dark animation="slideUp">
      <div className={menuSubcategoryLabel}>
        {t('lessons.editor.subMenuTitles.recorded')}
      </div>
      <Option
        label={t('lessons.editor.elements.webcam_recording')}
        onClick={e => handleClick(e)('Questions::WebcamRecording')}
        pendoClassName="trk_select_learning_add-webcam-recording-element"
      />

      <Option
        label={t('lessons.editor.elements.screen_recording')}
        onClick={e => handleClick(e)('Questions::ScreenRecording')}
        pendoClassName="trk_select_learning_add-screen-recording-element"
      />
      <Option
        label={t('lessons.editor.elements.audio_recording')}
        onClick={e => handleClick(e)('Questions::AudioRecording')}
        pendoClassName="trk_select_learning_add-audio-recording-element"
      />

      <Divider />
      <div className={menuSubcategoryLabel}>
        {t('lessons.editor.subMenuTitles.written')}
      </div>

      <Option
        label={t('lessons.editor.elements.written_response')}
        onClick={e => handleClick(e)('Questions::WrittenResponse')}
        pendoClassName="trk_select_learning_add-written-response-element"
      />
      <div>
        <Option
          label={t('lessons.editor.elements.email_composition')}
          onClick={e => handleClick(e)('Questions::EmailComposition')}
          pendoClassName="trk_select_learning_add-email-composition-element"
        />
        <Option
          label={t('lessons.editor.elements.email_reply')}
          onClick={e => handleClick(e)('Questions::EmailReply')}
          pendoClassName="trk_select_learning_add-email-reply-element"
        />
      </div>

      <Option
        label={t('lessons.editor.elements.chat')}
        onClick={e => handleClick(e)('Questions::Chat')}
        pendoClassName="trk_select_learning_add-chat-element"
      />
      <Option
        label={t('lessons.editor.elements.ticket_handling')}
        onClick={e => handleClick(e)('Questions::TicketHandling')}
        pendoClassName="trk_select_learning_add-ticket-handling-element"
      />
      {props.canUseZendeskPractice && (
        <Option
          label={t('lessons.editor.elements.zendesk_ticket_handling')}
          onClick={props.showZendeskLibrary}
          pendoClassName="trk_select_learning_add-zendesk-ticket-handling-element"
        />
      )}

      {(props.canUseConversationPractice || props.canUseRolePlay) && (
        <Divider />
      )}
      <div className={menuSubcategoryLabel}>
        {t('lessons.editor.subMenuTitles.nonGraded')}
      </div>
      {props.canUseConversationPractice && (
        <Option
          label={t('lessons.editor.elements.conversation')}
          onClick={e => handleClick(e)('Questions::Conversation')}
          pendoClassName="trk_select_learning_add-chat-conversation-element"
        />
      )}
      <Option
        label={t('lessons.editor.elements.role_play')}
        onClick={e => handleClick(e)('RolePlay')}
        pendoClassName="trk_select_learning_add-role-play-element"
      />
    </List>
  );

  const hasGongEnabled = integrations => {
    if (!integrations) return false;
    return integrations.find(e => e.integrationName && e.integrationEnabled);
  };

  const hasSeismicContentFeatureFlag = useFeatureFlags([
    'readiness-enable-embed-seismic-content',
  ]);

  const hasQuestionAssistantFeatureFlag = useFeatureFlags([
    'readiness-enable-question-assistant',
  ]);

  return (
    props.visible && (
      <List
        dark
        dynamic
        style={props.style}
        animation="slideIn"
        ref={node => props.setRef(node, 'menu')}
      >
        <div>
          <Option
            icon={<AlignLeft />}
            label={t('lessons.editor.elements.text')}
            onClick={e => handleClick(e)('paragraph')}
            pendoClassName="trk_select_readiness_add-text-element"
          />
          {!props.canUsePractice && (
            <Option
              icon={<HelpCircle />}
              label={t('lessons.editor.elements.question')}
              onClick={handleClick}
              nestedOptions={questionTypesList}
            />
          )}
          <Option
            icon={<FlipCardSvg />}
            label={t('lessons.editor.elements.flipcard')}
            onClick={e => handleClick(e)('flipcard')}
            pendoClassName="trk_select_readiness_add-flipcard-element"
          />
          <Option
            icon={<RevealSvg />}
            label={t('lessons.editor.elements.reveal')}
            onClick={e => handleClick(e)('reveal')}
            pendoClassName="trk_select_readiness_add-reveal-element"
          />
          <Option
            icon={<Image />}
            label={t('lessons.editor.elements.image')}
            onClick={e => handleClick(e)('lesson_content_image')}
            pendoClassName="trk_select_readiness_add-image-element"
          />
          <Option
            icon={<Heart />}
            label={t('lessons.editor.elements.embed')}
            onClick={e => handleClick(e)('embed')}
            pendoClassName="trk_select_readiness_add-embed-element"
          />
          {hasGongEnabled(props.integrations) && (
            <Option
              icon={<GongSvg />}
              label={t('lessons.editor.elements.gong_call')}
              onClick={e => handleClick(e)('gong_call')}
              pendoClassName="trk_select_learning_add-gong-call-element"
            />
          )}
          <Option
            icon={<Film />}
            label={t('lessons.editor.elements.video')}
            onClick={e => handleClick(e)('video')}
            pendoClassName="trk_select_readiness_add-video-element"
          />
          <Option
            icon={<Mic />}
            label={t('lessons.editor.elements.record_audio')}
            onClick={e => handleClick(e)('media_recording')}
            pendoClassName="trk_select_readiness_add-audio-element"
          />
          <Option
            icon={<FileText />}
            label={t('lessons.editor.elements.document')}
            onClick={e => handleClick(e)('document')}
            pendoClassName="trk_select_readiness_add-document-element"
          />
          {hasSeismicContentFeatureFlag && props.isJointUser && (
            <Option
              icon={<SeismicSvg />}
              label={t('lessons.editor.elements.content')}
              onClick={e => handleClick(e)('seismic_content')}
              pendoClassName="trk_select_readiness_add-content-element"
            />
          )}
          {props.canViewEvents && (
            <Option
              icon={<Calendar />}
              label={t('lessons.editor.elements.event')}
              onClick={e => handleClick(e)('lesson_event_element')}
              pendoClassName="trk_select_readiness_add-events-element"
            />
          )}
          {hasQuestionAssistantFeatureFlag && (
            <Option
              icon={<AISvg fill="#a3a3a2" />}
              label={t('lessons.editor.elements.generate')}
              onClick={props.showElementAssistantModal}
            />
          )}

          <span>
            <hr />
            <Option
              icon={<Check />}
              label={t('lessons.editor.elements.knowledge_check')}
              nestedOptions={knowledgeCheckTypesList}
            />
            {props.canUsePractice && (
              <span>
                <Option
                  icon={<HelpCircle />}
                  label={t('lessons.editor.elements.question')}
                  onClick={handleClick}
                  nestedOptions={questionTypesList}
                  pendoClassName="trk_select_readiness_add-question-element"
                />
                <Option
                  icon={<Star />}
                  label={t('lessons.editor.elements.practice')}
                  onClick={handleClick}
                  nestedOptions={practiceTypesList}
                  i18n={props.i18n.common}
                  pendoClassName="trk_select_readiness_add-practice-element"
                />
              </span>
            )}
          </span>
          <span>
            <hr />
            <Option
              icon={<Copy name="save_to_library" />}
              label={t('lessons.editor.elements.element_library')}
              onClick={props.showElementLibrary}
              pendoClassName="trk_select_readiness_element-library-from-add-element-dropdown"
            />
          </span>
        </div>
      </List>
    )
  );
};

const mapStateToProps = state => ({
  canUseConversationPractice: hasGlobalFeature(state, 'practice'),
  canUsePractice: hasGlobalFeature(state, 'practice'),
  canUseZendeskPractice: hasGlobalFeature(state, 'zendesk_practice'),
  canViewEvents: state.meta.currentUser.canViewEvents,
  isJointUser: state.meta.currentUser.isJointUser,
  i18n: state.editor.i18n,
  integrations: state.editor.integrations,
});

export default connect(mapStateToProps)(Menu);
